import type { LucideIcon } from 'lucide-react';

// Shared building blocks for the admin analytics pages.

export type IconType = LucideIcon;

export function Tile({ icon: Icon, tint, label, value }: { icon: IconType; tint: string; label: string; value: string }) {
  return (
    <div className="bg-white rounded-lg shadow-sm p-5">
      <div className="flex items-center space-x-3">
        <div className={`w-10 h-10 ${tint} rounded-lg flex items-center justify-center`}>
          <Icon className="w-5 h-5" />
        </div>
        <div>
          <p className="text-sm text-gray-500">{label}</p>
          <p className="text-2xl font-bold text-gray-900">{value}</p>
        </div>
      </div>
    </div>
  );
}

export interface Column<T> {
  header: string;
  render: (row: T) => string | number;
  align?: 'right';
}

export function RankedTable<T>({
  title,
  icon: Icon,
  rows,
  columns,
  empty,
  subtitle,
  onRowClick,
  isRowActive,
}: {
  title: string;
  icon: IconType;
  rows: T[];
  columns: Column<T>[];
  empty: string;
  subtitle?: string;
  /** Makes rows clickable (e.g. to filter the page by that row). */
  onRowClick?: (row: T) => void;
  isRowActive?: (row: T) => boolean;
}) {
  return (
    <div className="bg-white rounded-lg shadow-sm">
      <div className="flex items-center gap-2 px-5 py-4 border-b border-gray-100">
        <Icon size={16} className="text-gray-400" />
        <h2 className="text-sm font-semibold text-gray-900">{title}</h2>
        {subtitle && <span className="ml-auto text-xs text-gray-400">{subtitle}</span>}
      </div>
      {rows.length === 0 ? (
        <p className="px-5 py-6 text-sm text-gray-500">{empty}</p>
      ) : (
        <table className="w-full text-sm">
          <thead>
            <tr className="text-xs uppercase tracking-wide text-gray-500">
              {columns.map((c) => (
                <th
                  key={c.header}
                  className={`px-5 py-2 font-medium ${c.align === 'right' ? 'text-right' : 'text-left'}`}
                >
                  {c.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => (
              <tr
                key={i}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
                className={`border-t border-gray-50 ${onRowClick ? 'cursor-pointer hover:bg-gray-50' : ''} ${
                  isRowActive?.(row) ? 'bg-primary-50' : ''
                }`}
              >
                {columns.map((c) => (
                  <td
                    key={c.header}
                    className={`px-5 py-2 ${
                      c.align === 'right'
                        ? 'text-right tabular-nums text-gray-700'
                        : 'text-gray-900 truncate max-w-xs'
                    }`}
                  >
                    {c.render(row)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
